import { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebaseClient';

/**
 * Hook for polling Firebase custom claims until they're updated
 * This replaces manual token refresh logic with automatic polling
 */
interface PollingOptions {
  maxAttempts?: number;
  interval?: number;
  condition?: (claims: any) => boolean;
}

export interface PollingResult {
  startPolling: (customCondition?: ((claims: any) => boolean) | null) => Promise<any>;
  stopPolling: () => void;
  isPolling: boolean;
  attempts: number;
  claims: any;
  error: any;
  getClaims: () => Promise<any>;
}

export const useClaimsPolling = (options: PollingOptions = {}): PollingResult => {
  const {
    maxAttempts = 20,
    interval = 1000,
    condition = null
  } = options;

  const [isPolling, setIsPolling] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [claims, setClaims] = useState<any>(null);
  const [error, setError] = useState<any>(null);

  // Get current claims
  const getClaims = useCallback(async () => {
    try {
      if (!auth.currentUser) {
        return null;
      }

      // Force token refresh
      await auth.currentUser.getIdToken(true);

      // Get updated claims
      const tokenResult = await auth.currentUser.getIdTokenResult();
      return tokenResult.claims;
    } catch (err) {
      console.error('Error getting claims:', err);
      return null;
    }
  }, []);

  // Stop polling
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    setAttempts(0);
  }, []);

  // Start polling with condition
  const startPolling = useCallback(async (customCondition: ((claims: any) => boolean) | null = null) => {
    const checkCondition = customCondition || condition;

    if (!checkCondition) {
      throw new Error('No condition provided for claims polling');
    }

    setIsPolling(true);
    setAttempts(0);
    setError(null);

    return new Promise((resolve, reject) => {
      let attemptCount = 0;

      const pollInterval = setInterval(async () => {
        attemptCount++;
        setAttempts(attemptCount);

        try {
          const currentClaims = await getClaims();
          setClaims(currentClaims);

          // Check if condition is met
          if (checkCondition(currentClaims)) {
            clearInterval(pollInterval);
            setIsPolling(false);
            resolve(currentClaims);
            return;
          }

          // Check if max attempts reached
          if (attemptCount >= maxAttempts) {
            clearInterval(pollInterval);
            setIsPolling(false);
            const timeoutError = new Error('Claims polling timeout - max attempts reached');
            setError(timeoutError);
            reject(timeoutError);
            return;
          }
        } catch (err) {
          clearInterval(pollInterval);
          setIsPolling(false);
          setError(err);
          reject(err);
        }
      }, interval);

      // Cleanup function if component unmounts
      return () => {
        clearInterval(pollInterval);
        setIsPolling(false);
      };
    });
  }, [condition, maxAttempts, interval, getClaims]);

  return {
    startPolling,
    stopPolling,
    isPolling,
    attempts,
    claims,
    error,
    getClaims
  };
};

export interface WaitForTenantResult extends PollingResult {
  waitForTenant: () => Promise<any>;
}

/**
 * Specialized hook for waiting for tenant assignment
 * Polls until user has a tenantId in their custom claims
 */
export const useWaitForTenantAssignment = (): WaitForTenantResult => {
  const polling = useClaimsPolling({
    maxAttempts: 20,
    interval: 1000,
    condition: (claims) => claims && claims.tenantId !== null && claims.tenantId !== undefined
  });

  const waitForTenant = useCallback(() => {
    return polling.startPolling((claims) => {
      return claims && claims.tenantId !== null && claims.tenantId !== undefined;
    });
  }, [polling]);

  return {
    ...polling,
    waitForTenant
  };
};

export interface WaitForRoleResult extends PollingResult {
  waitForRole: () => Promise<any>;
}

/**
 * Specialized hook for waiting for role assignment
 * Polls until user has a role in their custom claims
 */
export const useWaitForRoleAssignment = (): WaitForRoleResult => {
  const polling = useClaimsPolling({
    maxAttempts: 15,
    interval: 1000,
    condition: (claims) => claims && claims.role !== null && claims.role !== undefined
  });

  const waitForRole = useCallback(() => {
    return polling.startPolling((claims) => {
      return claims && claims.role !== null && claims.role !== undefined;
    });
  }, [polling]);

  return {
    ...polling,
    waitForRole
  };
};
